import React, { useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    useExpanded,
    useFilters,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table';
import { Trans, withTranslation } from 'react-i18next';
import {
    Box,
    DaInput,
    SearchIcon,
    InternalGrid,
    ModalProvider,
    Card,
    Text,
    HeartIcon,
    SkeletonItem,
    Image,
    Link,
    ThemeDefault,
} from '@paygreen/paygreen-ui';
import {
    getNumberFromThemeValue,
    IsBigDesktop,
    IsFrench,
} from '../../utils/tools';
import { PaginationTable } from '../../components';
import SectorsDaTable from './SectorsDaTable';
import { useDebounce, useNodeDimensions } from '../../utils/hooks';

const SectorsTable = ({
    t,
    i18n,
    data,
    fetchData,
    userSector,
    updateUserSector,
    pageCount: controlledPageCount,
    isLoading,
    isLoadingUserData,
    filters,
    setFilters,
    setIsLoading,
}) => {
    const debouncedFilters = useDebounce(filters, 250, false);
    const [ref, { width }] = useNodeDimensions();

    const handleIndex = useCallback(
        (value) => {
            setFilters({ ...filters, pageIndex: value });
        },
        [setFilters, filters],
    );

    const handleSectorName = useCallback(
        (e) => {
            const { value } = e.target;

            if (
                // we start research only with minimum of 3 characters and when back to empty input
                !value.length ||
                value.length > 2
            ) {
                setIsLoading(true);
                setFilters({
                    ...filters,
                    pageIndex: 0,
                    sectorName: value,
                    activePageReset: true,
                });
            }
        },
        [setFilters, filters, setIsLoading],
    );

    const columns = useMemo(() => {
        return [
            {
                Header: 'ID',
                accessor: 'id',
            },
            {
                Header: t('sectors.table.name'),
                id: 'name',
                accessor: (row) => row[IsFrench(i18n, 'monetaryRatioName')],
                Filter: (
                    <DaInput
                        id="searchInput"
                        onChange={handleSectorName}
                        placeholder={t('user.list.table.input')}
                        fieldSize="sm"
                        icon={<SearchIcon />}
                        defaultValue={filters.sectorName}
                    />
                ),
            },
            {
                id: 'domain',
                Header: t('sectors.table.domain'),
                accessor: (row) =>
                    row[IsFrench(i18n, 'monetaryRatioCategoryName')],
            },
            {
                Header: t('sectors.table.emission'),
                accessor: 'emission',
            },
            {
                Header: 'Actions',
                accessor: '',
            },
        ];
    }, [filters, handleSectorName, t, i18n]);

    const setPageSize = (size) => {
        setFilters({
            ...filters,
            pageSize: size,
            pageIndex: 0,
            activePageReset: true,
        });
    };

    const { headerGroups, prepareRow, page, pageCount } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0, pageSize: 25, hiddenColumns: ['id'] },
            manualPagination: true,
            autoResetPage: false,
            pageCount: controlledPageCount,
            manualSortBy: true,
            autoResetFilters: false,
            // we ponctually override table internal state to resetPage index with component SelectColumnFilter when needed
            useControlledState: (state) => {
                return {
                    ...state,
                    pageIndex: debouncedFilters.pageIndex,
                    pageSize: debouncedFilters.pageSize,
                };
            },
        },
        useFilters,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect,
    );

    useEffect(() => {
        fetchData({
            debouncedFilters,
        });
    }, [fetchData, debouncedFilters]);

    return (
        <div ref={ref}>
            <InternalGrid
                displayType="grid"
                gridGap="md"
                gridTemplateColumns="1fr"
                blockPadding="none"
                justifyItems={IsBigDesktop() ? 'start' : 'stretch'}
                hasStaticWidth={IsBigDesktop() ? false : true}
            >
                <InternalGrid
                    gridTemplateColumns="auto 3fr 1fr"
                    displayType={
                        width >
                        getNumberFromThemeValue(ThemeDefault.blockWidth.xl)
                            ? 'grid'
                            : 'flex'
                    }
                    gridGap="md"
                    blockPadding="none"
                    justifyItems={IsBigDesktop() ? 'start' : 'stretch'}
                    alignItems="center"
                    style={{ flexDirection: 'column-reverse' }}
                >
                    <Box marginTop="md">
                        <Card
                            paddingBottom="sm"
                            paddingTop="sm"
                            paddingLateral="sm"
                            isShadowWab
                            blockWidth="sm"
                            radiusSize="sm"
                        >
                            <InternalGrid
                                justifyContent="flex-start"
                                childrenMarginBottom="md"
                                flexWrap="nowrap"
                            >
                                <Text htmlTag="strong" textSize="md">
                                    {t('sectors.favoriteTitle')}
                                </Text>
                                <HeartIcon marginLeft="md" iconSize="md" />
                            </InternalGrid>

                            <SkeletonItem
                                isLoading={isLoadingUserData}
                                colorWab="grey10"
                            >
                                {!userSector && (
                                    <Card
                                        hasBackground={true}
                                        colorPallet="wab"
                                        colorWab="grey10"
                                        paddingTop="xs"
                                        paddingBottom="xs"
                                        paddingLateral="xs"
                                        hasDashedBorder
                                        shadowSize="none"
                                        radiusSize="sm"
                                    >
                                        <Text
                                            colorPallet="wab"
                                            colorWab="grey50"
                                            textSize="sm"
                                            htmlTag="em"
                                        >
                                            {t('sectors.noFavorite')}
                                        </Text>
                                    </Card>
                                )}
                                {userSector && (
                                    <>
                                        <Text>
                                            {
                                                userSector[
                                                    IsFrench(
                                                        i18n,
                                                        'monetaryRatioName',
                                                    )
                                                ]
                                            }
                                        </Text>
                                        <Text
                                            textSize="sm"
                                            colorPallet="wab"
                                            colorWab="grey40"
                                        >
                                            {
                                                userSector[
                                                    IsFrench(
                                                        i18n,
                                                        'monetaryRatioCategoryName',
                                                    )
                                                ]
                                            }
                                        </Text>
                                        <Text
                                            textSize="sm"
                                            colorPallet="theme"
                                            htmlTag="strong"
                                        >
                                            {`${userSector.emission} ${t(
                                                'sectors.favoriteUnit',
                                            )}`}
                                        </Text>
                                    </>
                                )}
                            </SkeletonItem>
                        </Card>
                    </Box>

                    <Box
                        marginTop="md"
                        marginLeft={
                            width >
                            getNumberFromThemeValue(ThemeDefault.blockWidth.xl)
                                ? 'md'
                                : 'none'
                        }
                    >
                        <Image
                            imageType="cover"
                            style={{ width: '20%' }}
                            marginBottom="md"
                        >
                            <img
                                src="https://cdn.paygreen.fr/logo/partner/ademe.svg"
                                alt="ademe logo"
                            />
                        </Image>
                        <Text textSize="xs" colorPallet="wab" colorWab="grey60">
                            <Trans i18nKey="matching.ademeIntroduction1">
                                ...
                                <Text
                                    textSize="xs"
                                    colorPallet="wab"
                                    colorWab="grey60"
                                    htmlTag="strong"
                                >
                                    {{
                                        monetaryFactor: t(
                                            'matching.ademeIntroduction1Factors',
                                        ),
                                    }}
                                </Text>
                                ...
                            </Trans>
                        </Text>
                        <Text
                            textSize="xs"
                            colorPallet="wab"
                            colorWab="grey60"
                            marginTop="xs"
                        >
                            <Trans i18nKey="matching.ademeIntroduction2">
                                ...
                                <Text
                                    textSize="xs"
                                    colorPallet="wab"
                                    colorWab="grey60"
                                    htmlTag="strong"
                                >
                                    {{
                                        fromPrice: t(
                                            'matching.ademeIntroduction2From',
                                        ),
                                    }}
                                </Text>
                            </Trans>{' '}
                            {/* cannot apply Trans component on <link> and <a> */}
                            <a
                                href="https://www.bilans-ges.ademe.fr/documentation/UPLOAD_DOC_FR/index.htm?ratio-monetaires.htm"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Link
                                    colorPallet="theme"
                                    colorTheme="secondary"
                                    hasUnderline
                                >
                                    {t('matching.ademeIntroduction2Link')}
                                </Link>
                            </a>
                        </Text>
                    </Box>
                </InternalGrid>
                <ModalProvider>
                    <Box marginTop="lg">
                        <Text textSize="md" htmlTag="strong">
                            {t('sectors.table.title')}
                        </Text>
                    </Box>
                    <Box marginTop="md">
                        <SectorsDaTable
                            headerGroups={headerGroups}
                            prepareRow={prepareRow}
                            page={page}
                            isLoading={isLoading}
                            data={data}
                            userSector={userSector}
                            updateUserSector={updateUserSector}
                        />

                        {!!pageCount && (
                            <PaginationTable
                                setPageSize={setPageSize}
                                pageCount={pageCount}
                                pageIndex={debouncedFilters.pageIndex}
                                gotoPage={handleIndex}
                            />
                        )}
                    </Box>
                </ModalProvider>
            </InternalGrid>
        </div>
    );
};

SectorsTable.propTypes = {
    data: PropTypes.array.isRequired,
    fetchData: PropTypes.func.isRequired,
    pageCount: PropTypes.number,
    isLoading: PropTypes.bool,
    filters: PropTypes.object,
    setFilters: PropTypes.func,
    setIsLoading: PropTypes.func,
    userSector: PropTypes.object,
    updateUserSector: PropTypes.func,
    isLoadingUserData: PropTypes.bool,
};

SectorsTable.defaultProps = {
    isLoading: false,
    pageCount: 0,
};

export default withTranslation('common')(SectorsTable);
